// The staff PIN at shared counter devices — counter mode (spec/07 §6.7, spec/13 §1, spec/04 §2;
// F-IAM-10).
//
// A manager signs a front-desk device in once and turns it into a **shared device**: an organisation
// session for one venue, no longer the manager's own. From then on each member unlocks it with their
// own six-digit PIN, and every request it makes is theirs — their permissions, their venue scope,
// their name on the audit row (`session.staffPinHolderId`) — until they lock it, someone else
// unlocks it, or it has been idle for `staff.pinSessionMinutes` (default 15).
//
//   GET    /v1/organizations/{o}/members/me/pins             where you may set your PIN, and where it is set
//   PUT    /v1/organizations/{o}/members/me/pin              set or change your own PIN at a venue
//   DELETE /v1/organizations/{o}/members/{memberId}/pin      reset someone's PIN          members:update
//   POST   /v1/organizations/{o}/members/shared-device       turn this device into one    members:update
//   GET    /v1/organizations/{o}/members/shared-device       what the device shows: who holds it, who can
//   POST   /v1/organizations/{o}/members/{memberId}/unlock   unlock it as that member, with their PIN
//   POST   /v1/organizations/{o}/members/shared-device/lock  lock it again
//
// Every one but the list names its venue in `X-Clavian-Venue`: a PIN is set for one venue (spec/04 §2
// `staff_pin` has `venueId`), and a counter device stands at one. Closing counter mode is signing the
// device out (`POST /v1/auth/sign-out`).
//
// **Where you may set your PIN** is every venue you work at — every live venue of the organisation
// for an owner or a member with no venue scope, your own venues otherwise (spec/07 §6.1): exactly the
// venues `GET /v1/me/venues` lists for the organisation, and exactly the ones `GET …/members/me/pins`
// answers. Another organisation's venue, an archived one or one outside your scope is `404`.
//
// **The PIN is a quick switch, not a credential of its own.** It is hashed, keyed with the
// deployment's secret; five wrong PINs in a row lock it until a manager resets it; and it is not a
// second factor — a route that needs one still needs the device's session to have been established
// with one (spec/07 §6.6). Only its holder ever sets a PIN, from their own session: a reset clears
// it, and nobody else ever knows it, so an action on the device is the PIN holder's and nobody
// else's.
//
// Setting, resetting, locking and unlocking are audited, one row each (spec/07 §11); an unlock's row
// names the PIN holder as its actor.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MEMBERS_UPDATE } from "../permissions/index.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { RoleNameSchema } from "./members.js";
/** Six digits (spec/07 §6.7). */
export const STAFF_PIN_LENGTH = 6;
/** Wrong PINs in a row that lock a PIN until a manager resets it (spec/07 §6.7). */
export const STAFF_PIN_MAX_FAILURES = 5;
export const StaffPinSchema = z
    .string()
    .regex(/^\d{6}$/, 'A staff PIN is six digits')
    .openapi('StaffPin', { description: 'Six digits. Never returned, never logged, never in an audit row.' });
export const SetStaffPinSchema = z
    .object({
    /**
     * The new PIN. Six digits that are not one digit repeated (`111111`) nor a run up or down
     * (`123456`, `987654`): the first PINs anyone would try at a counter.
     */
    pin: StaffPinSchema,
})
    .strict()
    .openapi('SetStaffPin');
export const MyStaffPinSchema = z
    .object({
    venueId: VenueIdSchema,
    /** The venue's name, as `GET /v1/me/venues` gives it. */
    venueName: z.string(),
    /** You have a PIN here. Never the PIN itself, nor anything made from it. */
    set: z.boolean(),
    /** Locked after five wrong tries: a manager must reset it before you can set a new one. */
    locked: z.boolean(),
})
    .openapi('MyStaffPin', { description: 'One venue you work at, and whether your staff PIN is set there.' });
export const MyStaffPinListSchema = z
    .object({
    /** Every venue of the organisation you work at, oldest first — the venues `GET /v1/me/venues` lists for it. */
    data: z.array(MyStaffPinSchema),
})
    .openapi('MyStaffPinList');
export const UnlockSharedDeviceSchema = z.object({ pin: StaffPinSchema }).strict().openapi('UnlockSharedDevice');
/** The venue a PIN or a counter device belongs to: `X-Clavian-Venue`, required on every route here. */
export const StaffPinVenueHeaderSchema = z
    .object({
    'x-clavian-venue': VenueIdSchema.openapi({ description: 'The venue the PIN is for, or the counter device stands at.' }),
})
    .openapi('StaffPinVenueHeaders');
export const SharedDeviceHolderSchema = z
    .object({
    memberId: MemberIdSchema,
    /** What the console's header shows. `null` for someone who has not given a name. */
    name: z.string().nullable(),
    role: RoleNameSchema,
})
    .openapi('SharedDeviceHolder', { description: 'Who holds the PIN session on a counter device.' });
export const SharedDeviceStaffSchema = z
    .object({
    memberId: MemberIdSchema,
    name: z.string().nullable(),
    /** Locked after five wrong PINs: this member cannot unlock until a manager resets their PIN. */
    pinLocked: z.boolean(),
})
    .openapi('SharedDeviceStaff', { description: 'Someone who can unlock this counter device: a PIN at its venue.' });
export const SharedDeviceSchema = z
    .object({
    organizationId: OrganizationIdSchema,
    venueId: VenueIdSchema,
    /** Nobody holds the PIN session: every request but these few answers `device-locked`. */
    locked: z.boolean(),
    holder: SharedDeviceHolderSchema.nullable(),
    /** `staff.pinSessionMinutes` at this venue: idle this long, and the device locks itself. */
    idleLockMinutes: z.int().min(1),
    /**
     * The members who work at this venue and have a PIN here, by name — who the PIN pad offers.
     * Empty while the device is not a shared one.
     */
    staff: z.array(SharedDeviceStaffSchema),
})
    .openapi('SharedDevice', { description: 'A shared counter device: its venue and who holds its PIN session.' });
const MemberParamsSchema = OrganizationScopeParamsSchema.extend({ memberId: MemberIdSchema }).openapi('StaffPinMemberParams');
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const notFound = problemResponse('No such organisation, venue or member, or not one you can see.', 'not-found');
export const listMyStaffPinsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/members/me/pins',
    operationId: 'listMyStaffPins',
    tags: ['members'],
    summary: 'Where you may set your staff PIN, and where it is set',
    description: 'Every venue of the organisation you work at — every live venue for an owner or a member with no venue scope, ' +
        'your own venues otherwise — with whether your PIN is set and whether it is locked. Exactly the venues ' +
        '`PUT …/members/me/pin` accepts. Your own, from your own session: a counter device is refused.',
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(MyStaffPinListSchema, 'The venues, oldest first.'),
        401: commonProblemResponses[401],
        403: problemResponse('This is a counter device rather than your own session.', 'shared-device'),
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const setMyStaffPinRoute = createRoute({
    method: 'put',
    path: '/v1/organizations/{organizationId}/members/me/pin',
    operationId: 'setMyStaffPin',
    tags: ['members'],
    summary: 'Set or change your own staff PIN at a venue',
    description: 'From your own signed-in session — never from a counter device — for a venue you work at: any live venue of ' +
        'the organisation for an owner or a member with no venue scope, one of your own venues otherwise (the venues ' +
        '`GET …/members/me/pins` lists; anything else is `404`). Setting a new PIN replaces the old one. A PIN locked ' +
        'after five wrong tries stays locked until a manager resets it.',
    request: { params: OrganizationScopeParamsSchema, headers: StaffPinVenueHeaderSchema, ...json(SetStaffPinSchema) },
    responses: {
        204: { description: 'Set.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('Your PIN here is locked, or this is a counter device rather than your own session.', 'staff-pin-locked', 'shared-device'),
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const resetStaffPinRoute = createRoute({
    method: 'delete',
    path: '/v1/organizations/{organizationId}/members/{memberId}/pin',
    operationId: 'resetStaffPin',
    tags: ['members'],
    summary: 'Reset a member’s staff PIN at a venue',
    description: 'Clears their PIN and its lock; they set a new one from their own session. The member’s role must be below ' +
        'your own, and they must work only at venues you do (spec/07 §6.5). Nobody resets their own.',
    [PERMISSION_EXTENSION]: MEMBERS_UPDATE,
    request: { params: MemberParamsSchema, headers: StaffPinVenueHeaderSchema },
    responses: {
        204: { description: 'Reset, or there was no PIN to reset.' },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const openSharedDeviceRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/members/shared-device',
    operationId: 'openSharedDevice',
    tags: ['members'],
    summary: 'Turn this device into a shared counter device',
    description: 'The session making the request stops being yours and becomes the counter device’s, at the venue named: ' +
        'locked until someone unlocks it with their PIN. Your own account can no longer be changed from it; sign it ' +
        'out to end counter mode. The second factor the session was signed in with stays with it, and is what the ' +
        'console’s second-factor rule reads for every PIN holder.',
    [PERMISSION_EXTENSION]: MEMBERS_UPDATE,
    request: { params: OrganizationScopeParamsSchema, headers: StaffPinVenueHeaderSchema },
    responses: {
        200: jsonResponse(SharedDeviceSchema, 'The counter device, locked.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('This session is already a counter device, or lacks the permission.', 'shared-device', 'forbidden'),
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getSharedDeviceRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/members/shared-device',
    operationId: 'getSharedDevice',
    tags: ['members'],
    summary: 'This counter device: who holds it, and who can unlock it',
    description: 'Answers a locked counter device too — it is what the PIN pad is drawn from. Not a counter device: `404`.',
    request: { params: OrganizationScopeParamsSchema, headers: StaffPinVenueHeaderSchema },
    responses: {
        200: jsonResponse(SharedDeviceSchema, 'The counter device.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const unlockSharedDeviceRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/members/{memberId}/unlock',
    operationId: 'unlockSharedDevice',
    tags: ['members'],
    summary: 'Unlock this counter device as a member, with their PIN',
    description: 'Only on a counter device, and only for a member who works at its venue and has a PIN there. From then on ' +
        'every request the device makes is theirs, until it is locked, someone else unlocks it, or it has been idle ' +
        `for \`staff.pinSessionMinutes\`. ${STAFF_PIN_MAX_FAILURES} wrong PINs in a row lock that member’s PIN until a ` +
        'manager resets it; everyone else can still unlock.',
    request: { params: MemberParamsSchema, headers: StaffPinVenueHeaderSchema, ...json(UnlockSharedDeviceSchema) },
    responses: {
        200: jsonResponse(SharedDeviceSchema, 'The counter device, unlocked for them.'),
        400: problemResponse('The PIN is wrong, or the member has no PIN at this venue.', 'staff-pin-incorrect', 'validation-failed'),
        401: commonProblemResponses[401],
        403: problemResponse('That member’s PIN is locked; a manager must reset it.', 'staff-pin-locked'),
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const lockSharedDeviceRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/members/shared-device/lock',
    operationId: 'lockSharedDevice',
    tags: ['members'],
    summary: 'Lock this counter device',
    description: 'The PIN holder steps away; the next person unlocks with their own PIN. Locking a locked device changes nothing.',
    request: { params: OrganizationScopeParamsSchema, headers: StaffPinVenueHeaderSchema },
    responses: {
        200: jsonResponse(SharedDeviceSchema, 'The counter device, locked.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const STAFF_PIN_ROUTES = [
    listMyStaffPinsRoute,
    setMyStaffPinRoute,
    resetStaffPinRoute,
    openSharedDeviceRoute,
    getSharedDeviceRoute,
    unlockSharedDeviceRoute,
    lockSharedDeviceRoute,
];
//# sourceMappingURL=staff-pin.js.map